import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../Config/config";
import ToastAlert, { showToast } from "../Common/ToastAlert";

const freqOptions = [
    { value: "weekly", label: "Weekly" },
    { value: "biweekly", label: "Bi-Weekly" },
    { value: "monthly", label: "Monthly" },
];

const dowOptions = [
    { v: 1, label: "Mon" },
    { v: 2, label: "Tue" },
    { v: 3, label: "Wed" },
    { v: 4, label: "Thu" },
    { v: 5, label: "Fri" },
    { v: 6, label: "Sat" },
    { v: 7, label: "Sun" },
];

// ✅ Targeting options (matches backend normalizeResendFilter/normalizeTargeting)
const targetingModeOptions = [
    { value: "unsent", label: "Unsent only" },
    { value: "resend", label: "Resend" },
];

const resendFilterOptions = [
    { value: "all", label: "All previously sent" },
    { value: "opened", label: "Opened" },
    { value: "not_opened", label: "Not opened" },
    { value: "clicked_explorer", label: "Clicked: Explorer" },
    { value: "clicked_meeting", label: "Clicked: Meeting" },
    { value: "clicked_youtube", label: "Clicked: YouTube" },
    { value: "clicked_unsubscribe", label: "Clicked: Unsubscribe" },
];

function fmtTs(ts) {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function normalizeMode(v) {
    const s = String(v || "unsent").trim().toLowerCase();
    return s === "resend" ? "resend" : "unsent";
}

function normalizeResendFilter(v) {
    const s = String(v || "all").trim().toLowerCase();
    const allowed = new Set(resendFilterOptions.map((o) => o.value));
    return allowed.has(s) ? s : "all";
}

function getDefaultForm(categories, templates) {
    return {
        name: "",
        enabled: true,
        categoryId: categories?.[0]?.id || "",
        templateId: templates?.[0]?.id || "",
        frequency: "weekly",
        batchSize: 30,
        schedule: {
            timeZone: "America/Indiana/Indianapolis",
            dayOfWeek: 1,
            dayOfMonth: 1,
        },
        // ✅ NEW: targeting
        targeting: {
            mode: "unsent",
            resendFilter: "all",
        },
    };
}

export default function MarketingCampaignsAdmin() {
    const [userId, setUserId] = useState(() => localStorage.getItem("userId") || "");
    const [subscriptionId, setSubscriptionId] = useState(() => localStorage.getItem("subscriptionId") || "");
    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState([]);
    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const [form, setForm] = useState(() => getDefaultForm([], []));

    useEffect(() => {
        const uid = localStorage.getItem("userId") || "";
        if (uid && uid !== userId) setUserId(uid);
    }, [userId]);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${config.API_BASE_URL}/get-user-details?userId=${encodeURIComponent(userId)}`);
                const customSubObj =
                    data?.customSubscription && typeof data.customSubscription === "object" ? data.customSubscription : null;
                let subFromUser = "";
                if (customSubObj) {
                    const entries = Object.entries(customSubObj)
                        .map(([, v]) => String(v || "").trim())
                        .filter(Boolean);
                    if (entries.length) subFromUser = entries[entries.length - 1];
                }
                const nextSubId = String(subFromUser || data?.subscriptionId || localStorage.getItem("subscriptionId") || "").trim();
                if (!cancelled && nextSubId) {
                    setSubscriptionId(nextSubId);
                    localStorage.setItem("subscriptionId", nextSubId);
                }
            } catch {
                // best effort only
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId]);

    const orgHeaders = useMemo(() => {
        const h = {};
        if (userId) h["x-user-id"] = userId;
        if (subscriptionId) h["x-subscription-id"] = subscriptionId;
        return h;
    }, [userId, subscriptionId]);

    const withOrgHeaders = (cfg = {}) => ({
        ...cfg,
        headers: { ...(orgHeaders || {}), ...(cfg?.headers || {}) },
    });

    const categoryById = useMemo(() => {
        const m = new Map();
        categories.forEach((c) => m.set(c.id, c));
        return m;
    }, [categories]);

    const templateById = useMemo(() => {
        const m = new Map();
        templates.forEach((t) => m.set(t.id, t));
        return m;
    }, [templates]);

    async function fetchAll() {
        setLoading(true);
        try {
            const [catsRes, tmplsRes, campsRes] = await Promise.all([
                axios.get(`${config.API_BASE_URL}/categories`, withOrgHeaders()),
                axios.get(`${config.API_BASE_URL}/email-templates`, withOrgHeaders()),
                axios.get(`${config.API_BASE_URL}/marketing-campaigns`, withOrgHeaders()),
            ]);

            const cats = catsRes?.data;
            const tmpls = tmplsRes?.data;
            const camps = campsRes?.data;

            setCategories(Array.isArray(cats) ? cats : []);
            setTemplates(Array.isArray(tmpls) ? tmpls : []);
            setCampaigns(Array.isArray(camps) ? camps : []);
        } catch (e) {
            console.error("Failed to load marketing campaign admin data:", e);
            showToast("Failed to load campaigns", "error");
            setCategories([]);
            setTemplates([]);
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptionId]);

    function openCreate() {
        setEditing(null);
        setForm(getDefaultForm(categories, templates));
        setOpen(true);
    }

    function openEdit(c) {
        setEditing(c);

        const mode = normalizeMode(c?.targeting?.mode);
        const resendFilter = normalizeResendFilter(c?.targeting?.resendFilter);

        setForm({
            name: c.name || "",
            enabled: !!c.enabled,
            categoryId: c.categoryId || "",
            templateId: c.templateId || "",
            frequency: c.frequency || "weekly",
            batchSize: Number(c.batchSize || 30),
            schedule: {
                timeZone: c.schedule?.timeZone || "America/Indiana/Indianapolis",
                dayOfWeek: Number(c.schedule?.dayOfWeek || 1),
                dayOfMonth: Number(c.schedule?.dayOfMonth || 1),
            },
            // ✅ NEW: targeting (safe defaults if missing on older docs)
            targeting: {
                mode,
                resendFilter,
            },
        });
        setOpen(true);
    }

    function setTargeting(patch) {
        setForm((p) => ({
            ...p,
            targeting: {
                ...(p.targeting || { mode: "unsent", resendFilter: "all" }),
                ...patch,
            },
        }));
    }

    async function save() {
        // Basic guardrails (optional)
        if (!String(form.name || "").trim()) {
            showToast("Name is required", "error");
            return;
        }
        if (!form.categoryId) {
            showToast("Category is required", "error");
            return;
        }
        if (!form.templateId) {
            showToast("Template is required", "error");
            return;
        }

        const mode = normalizeMode(form?.targeting?.mode);
        const resendFilter = normalizeResendFilter(form?.targeting?.resendFilter);

        const payload = {
            ...form,
            batchSize: Number(form.batchSize || 10),
            schedule: {
                ...form.schedule,
                dayOfWeek: Number(form.schedule.dayOfWeek || 1),
                dayOfMonth: Number(form.schedule.dayOfMonth || 1),
            },
            targeting: {
                mode,
                resendFilter,
            },
            recalculateNextRun: true,
        };

        // If unsent mode, resendFilter is ignored by backend, but we keep it stored.
        // If you prefer to clear it when unsent:
        // if (payload.targeting.mode !== "resend") payload.targeting.resendFilter = "all";

        const isEdit = !!editing?.id;
        try {
            if (isEdit) {
                await axios.put(`${config.API_BASE_URL}/marketing-campaigns/${editing.id}`, payload, withOrgHeaders());
            } else {
                await axios.post(`${config.API_BASE_URL}/marketing-campaigns`, payload, withOrgHeaders());
            }

            showToast("Campaign saved", "success");
            setOpen(false);
            await fetchAll();
        } catch (e) {
            console.error("Save failed:", e);
            const msg = e?.response?.data?.message || "Save failed";
            showToast(msg, "error");
        }
    }

    async function toggleEnabled(c) {
        try {
            await axios.put(`${config.API_BASE_URL}/marketing-campaigns/${c.id}`, {
                enabled: !c.enabled,
            }, withOrgHeaders());
            showToast(c.enabled ? "Campaign disabled" : "Campaign enabled", "success");
            await fetchAll();
        } catch (e) {
            console.error("Toggle enabled failed:", e);
            showToast("Failed to update campaign", "error");
        }
    }

    async function runNow(c) {
        try {
            const res = await axios.post(`${config.API_BASE_URL}/marketing-campaigns/${c.id}/run-now`, {}, withOrgHeaders());
            const sent = res?.data?.result?.sent ?? 0;
            showToast(`Run completed. Sent: ${sent}`, "success");
            await fetchAll();
        } catch (e) {
            console.error("Run now failed:", e);
            const msg = e?.response?.data?.message || "Run failed";
            showToast(msg, "error");
        }
    }

    async function remove(c) {
        if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
        try {
            await axios.delete(`${config.API_BASE_URL}/marketing-campaigns/${c.id}`, withOrgHeaders());
            showToast("Campaign deleted", "success");
            await fetchAll();
        } catch (e) {
            console.error("Delete failed:", e);
            showToast("Failed to delete campaign", "error");
        }
    }

    function renderTargetingSummary(c) {
        const mode = normalizeMode(c?.targeting?.mode);
        const rf = normalizeResendFilter(c?.targeting?.resendFilter);
        if (mode === "unsent") return "Unsent";
        const label = resendFilterOptions.find((o) => o.value === rf)?.label || rf;
        return `Resend · ${label}`;
    }

    return (
        <div className="ln-page-shell">
            <div className="crd panel-gradient p-4">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-xl font-semibold">Marketing Campaigns</div>
                        <div className="text-sm opacity-80">
                            Scheduler runs daily at <b>7:00 AM</b>. Each due campaign sends up to its batch size based on targeting.
                        </div>
                    </div>

                    <button className="btn-primary" onClick={openCreate}>
                        + New Campaign
                    </button>
                </div>

                <div className="section-divider my-4" />

                {loading ? (
                    <div className="opacity-70">Loading…</div>
                ) : campaigns.length === 0 ? (
                    <div className="opacity-70">No campaigns yet.</div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block">
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="text-left">
                                            <th className="p-2 border-b">Name</th>
                                            <th className="p-2 border-b">Category</th>
                                            <th className="p-2 border-b">Template</th>
                                            <th className="p-2 border-b">Frequency</th>
                                            <th className="p-2 border-b">Targeting</th>
                                            <th className="p-2 border-b">Batch</th>
                                            <th className="p-2 border-b">Next Run</th>
                                            <th className="p-2 border-b">Enabled</th>
                                            <th className="p-2 border-b w-[260px]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map((c) => (
                                            <tr key={c.id} className="border-b">
                                                <td className="p-2 font-medium">{c.name}</td>
                                                <td className="p-2">{categoryById.get(c.categoryId)?.name || c.categoryId}</td>
                                                <td className="p-2">{templateById.get(c.templateId)?.name || c.templateId}</td>
                                                <td className="p-2">
                                                    {c.frequency}
                                                    {c.frequency === "monthly" ? ` (day ${c.schedule?.dayOfMonth || 1})` : ""}
                                                    {c.frequency !== "monthly"
                                                        ? ` (${dowOptions.find((d) => d.v === Number(c.schedule?.dayOfWeek || 1))?.label || "Mon"
                                                        })`
                                                        : ""}
                                                </td>

                                                <td className="p-2">
                                                    <span className="badge-soft-info px-2 py-1 rounded-full">
                                                        {renderTargetingSummary(c)}
                                                    </span>
                                                </td>

                                                <td className="p-2">{c.batchSize}</td>
                                                <td className="p-2">{fmtTs(c.nextRunAt)}</td>
                                                <td className="p-2">{c.enabled ? "Yes" : "No"}</td>
                                                <td className="p-2">
                                                    <div className="flex flex-wrap gap-2">
                                                        <button className="btn-secondary" onClick={() => openEdit(c)}>
                                                            Edit
                                                        </button>
                                                        <button className="btn-ghost" onClick={() => toggleEnabled(c)}>
                                                            {c.enabled ? "Disable" : "Enable"}
                                                        </button>
                                                        <button className="btn-secondary" onClick={() => runNow(c)}>
                                                            Run now
                                                        </button>
                                                        <button className="btn-ghost" onClick={() => remove(c)}>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden grid gap-3">
                            {campaigns.map((c) => (
                                <div key={c.id} className="crd p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-semibold">{c.name}</div>
                                            <div className="text-sm opacity-80">
                                                {categoryById.get(c.categoryId)?.name || c.categoryId} ·{" "}
                                                {templateById.get(c.templateId)?.name || c.templateId}
                                            </div>

                                            <div className="text-sm mt-2 flex flex-wrap gap-2">
                                                <span className="pill-label">Targeting</span>
                                                <span className="pill-value">{renderTargetingSummary(c)}</span>
                                            </div>

                                            <div className="text-sm mt-1">
                                                <span className="pill-label">Freq</span>{" "}
                                                <span className="pill-value">{c.frequency}</span>{" "}
                                                <span className="pill-label">Batch</span>{" "}
                                                <span className="pill-value">{c.batchSize}</span>
                                            </div>
                                            <div className="text-sm mt-1">
                                                <span className="pill-label">Next</span>{" "}
                                                <span className="pill-value">{fmtTs(c.nextRunAt)}</span>
                                            </div>
                                        </div>
                                        <div className="text-xs badge-soft-info px-2 py-1 rounded-full">
                                            {c.enabled ? "Enabled" : "Disabled"}
                                        </div>
                                    </div>

                                    <div className="section-divider my-3" />

                                    <div className="flex flex-wrap gap-2">
                                        <button className="btn-secondary" onClick={() => openEdit(c)}>
                                            Edit
                                        </button>
                                        <button className="btn-ghost" onClick={() => toggleEnabled(c)}>
                                            {c.enabled ? "Disable" : "Enable"}
                                        </button>
                                        <button className="btn-secondary" onClick={() => runNow(c)}>
                                            Run now
                                        </button>
                                        <button className="btn-ghost" onClick={() => remove(c)}>
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Simple modal */}
            {open && (
                <div className="dialog-shell">
                    <div className="crd dialog-header p-4">
                        <div className="text-lg font-semibold">{editing ? "Edit Campaign" : "New Campaign"}</div>
                        <div className="text-sm opacity-80">Runs at 7:00 AM in the campaign timezone.</div>
                    </div>

                    <div className="crd p-4 mt-2">
                        <div className="grid gap-3">
                            <label className="grid gap-1">
                                <span className="text-sm opacity-80">Name</span>
                                <input
                                    className="w-full p-2 rounded border"
                                    value={form.name}
                                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                />
                            </label>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Category</span>
                                    <select
                                        className="w-full p-2 rounded border"
                                        value={form.categoryId}
                                        onChange={(e) => setForm((p) => ({ ...p, categoryId: e.target.value }))}
                                    >
                                        {categories.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Template</span>
                                    <select
                                        className="w-full p-2 rounded border"
                                        value={form.templateId}
                                        onChange={(e) => setForm((p) => ({ ...p, templateId: e.target.value }))}
                                    >
                                        {templates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {/* ✅ NEW: Targeting Controls */}
                            <div className="crd p-3 border">
                                <div className="text-sm font-semibold mb-2">Targeting</div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className="grid gap-1">
                                        <span className="text-sm opacity-80">Mode</span>
                                        <select
                                            className="w-full p-2 rounded border"
                                            value={normalizeMode(form?.targeting?.mode)}
                                            onChange={(e) => {
                                                const nextMode = normalizeMode(e.target.value);
                                                // keep resendFilter selection, but UX: default to "all" when switching into resend
                                                setTargeting({
                                                    mode: nextMode,
                                                    resendFilter:
                                                        nextMode === "resend"
                                                            ? normalizeResendFilter(form?.targeting?.resendFilter)
                                                            : normalizeResendFilter(form?.targeting?.resendFilter),
                                                });
                                            }}
                                        >
                                            {targetingModeOptions.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="grid gap-1">
                                        <span className="text-sm opacity-80">Resend filter</span>
                                        <select
                                            className="w-full p-2 rounded border"
                                            value={normalizeResendFilter(form?.targeting?.resendFilter)}
                                            onChange={(e) => setTargeting({ resendFilter: normalizeResendFilter(e.target.value) })}
                                            disabled={normalizeMode(form?.targeting?.mode) !== "resend"}
                                            title={
                                                normalizeMode(form?.targeting?.mode) !== "resend"
                                                    ? "Enable Resend mode to use a resend filter"
                                                    : ""
                                            }
                                        >
                                            {resendFilterOptions.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div className="text-xs opacity-70 mt-2">
                                    Unsent mode sends only to contacts with <b>emailSent=false</b>. Resend mode sends only to{" "}
                                    <b>emailSent=true</b> and then applies the resend filter using stored analytics.
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Frequency</span>
                                    <select
                                        className="w-full p-2 rounded border"
                                        value={form.frequency}
                                        onChange={(e) => setForm((p) => ({ ...p, frequency: e.target.value }))}
                                    >
                                        {freqOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Batch size</span>
                                    <input
                                        type="number"
                                        className="w-full p-2 rounded border"
                                        value={form.batchSize}
                                        onChange={(e) => setForm((p) => ({ ...p, batchSize: e.target.value }))}
                                        min={1}
                                        max={500}
                                    />
                                </label>

                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Timezone</span>
                                    <input
                                        className="w-full p-2 rounded border"
                                        value={form.schedule.timeZone}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, schedule: { ...p.schedule, timeZone: e.target.value } }))
                                        }
                                    />
                                </label>
                            </div>

                            {form.frequency === "monthly" ? (
                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Day of month</span>
                                    <input
                                        type="number"
                                        className="w-full p-2 rounded border"
                                        value={form.schedule.dayOfMonth}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                schedule: { ...p.schedule, dayOfMonth: e.target.value },
                                            }))
                                        }
                                        min={1}
                                        max={31}
                                    />
                                </label>
                            ) : (
                                <label className="grid gap-1">
                                    <span className="text-sm opacity-80">Day of week</span>
                                    <select
                                        className="w-full p-2 rounded border"
                                        value={form.schedule.dayOfWeek}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                schedule: { ...p.schedule, dayOfWeek: e.target.value },
                                            }))
                                        }
                                    >
                                        {dowOptions.map((d) => (
                                            <option key={d.v} value={d.v}>
                                                {d.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={form.enabled}
                                    onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                                />
                                <span className="text-sm">Enabled</span>
                            </label>
                        </div>

                        <div className="dialog-actions-bar mt-4 flex justify-end gap-2">
                            <button className="btn-ghost" onClick={() => setOpen(false)}>
                                Cancel
                            </button>
                            <button className="btn-primary" onClick={save}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ToastAlert />
        </div>
    );
}
